import type { ReactElement } from 'react';
import type { RoleAssignment } from '@citibox-panel/api-client';
import {
  Box,
  EditIcon,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Text,
} from '@citibox-panel/ui';
import { canonicalEmail } from '../lib/email';
import { RoleChip } from './RoleChip';

// TODO: i18n
const COPY = {
  table: 'Asignaciones',
  email: 'Email',
  roles: 'Roles',
  extras: 'Permisos extra',
  actions: 'Acciones',
  self: ' (tú)',
  readOnly: 'Gestionado por backend',
  none: '—',
  edit: (email: string) => `Editar ${email}`,
} as const;

export interface ChipModel {
  key: string;
  label: string;
  unknown: boolean;
}

export interface AssignmentTableRow {
  assignment: RoleAssignment;
  readOnly: boolean;
  roleChips: ChipModel[];
  extraChips: ChipModel[];
}

export interface AssignmentsTableProps {
  rows: readonly AssignmentTableRow[];
  selfEmail: string | null;
  onEdit: (email: string) => void;
  // Lets the page return focus to a row's ✎ after a save, without the table owning focus policy.
  editButtonRef?: (email: string, element: HTMLButtonElement | HTMLAnchorElement | null) => void;
}

function Chips({ chips }: { chips: readonly ChipModel[] }): ReactElement {
  if (chips.length === 0) {
    return (
      <Text variant="body2" color="muted">
        {COPY.none}
      </Text>
    );
  }
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
      {chips.map((chip) => (
        <RoleChip key={chip.key} label={chip.label} unknown={chip.unknown} />
      ))}
    </Box>
  );
}

// The rows are a list to read, not targets: the only tab stop per row is its ✎, so a keyboard user
// is not walked through every cell to reach the next person.
export function AssignmentsTable({
  rows,
  selfEmail,
  onEdit,
  editButtonRef,
}: AssignmentsTableProps): ReactElement {
  const self = selfEmail === null ? null : canonicalEmail(selfEmail);
  return (
    <Table aria-label={COPY.table}>
      <TableHead>
        <TableRow>
          <TableCell component="th" scope="col">
            {COPY.email}
          </TableCell>
          <TableCell component="th" scope="col">
            {COPY.roles}
          </TableCell>
          <TableCell component="th" scope="col">
            {COPY.extras}
          </TableCell>
          <TableCell component="th" scope="col" align="right">
            {COPY.actions}
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map(({ assignment, readOnly, roleChips, extraChips }) => {
          const { email } = assignment;
          return (
            <TableRow key={email}>
              <TableCell component="th" scope="row">
                <Text component="span" variant="body2">
                  {email}
                </Text>
                {email === self && (
                  <Text component="span" variant="body2" color="muted">
                    {COPY.self}
                  </Text>
                )}
              </TableCell>
              <TableCell>
                <Chips chips={roleChips} />
              </TableCell>
              <TableCell>
                <Chips chips={extraChips} />
              </TableCell>
              <TableCell align="right">
                {readOnly ? (
                  <Text variant="body2" color="muted">
                    {COPY.readOnly}
                  </Text>
                ) : (
                  <IconButton
                    aria-label={COPY.edit(email)}
                    onClick={() => {
                      onEdit(email);
                    }}
                    {...(editButtonRef
                      ? {
                          ref: (element: HTMLButtonElement | HTMLAnchorElement | null) => {
                            editButtonRef(email, element);
                          },
                        }
                      : {})}
                  >
                    <EditIcon />
                  </IconButton>
                )}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
